import { useEffect, useState } from 'react'
import { useForm, useFormState, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { StepHeader } from '@/components/activation/StepHeader'
import { AskRobPanel } from '@/components/activation/AskRobPanel'
import { LabeledInput } from '@/components/activation/LabeledInput'
import { StepNavigation } from '@/components/activation/StepNavigation'
import { TargetTransitionBridge } from '@/components/activation/TargetTransitionBridge'
import { digitsOnly, INPUT_PATTERNS } from '@/components/activation/input-sanitizers'
import { targetSchema } from '@/components/activation/schemas/target'
import { useAskRobChat } from '@/hooks/use-ask-rob-chat'
import { useActivation } from '@/lib/activation-context'
import { buildTargetBridge, numericTargetValue } from '@/lib/activation-target-bridge'
import { useSession } from '@/lib/session-context'

const PROMPTS = [
  'Help me set my investment targets',
]

const TARGET_FIELDS = [
  'targetMonthlyCashflowGross',
  'timeframeYears',
  'availableCapital',
  'jvCapitalAccess',
]

function cleanTargetValue(value) {
  return value?.toString() ?? ''
}

function targetValuesFromObject(values) {
  return Object.fromEntries(TARGET_FIELDS.map((field) => [field, cleanTargetValue(values?.[field])]))
}

function targetValuesFromArray(values) {
  return Object.fromEntries(TARGET_FIELDS.map((field, index) => [field, cleanTargetValue(values?.[index])]))
}

function targetValuesMatch(left, right) {
  return Boolean(left) && TARGET_FIELDS.every((field) => cleanTargetValue(left[field]) === cleanTargetValue(right[field]))
}

function targetValuesPresent(values) {
  return TARGET_FIELDS.some((field) => cleanTargetValue(values[field]))
}

export function TargetStep() {
  const { step1, saveStep, askStepOneAgent, serverError } = useActivation()
  const { stepOneFlowVersion } = useSession()
  const navigate = useNavigate()
  const form = useForm({
    resolver: zodResolver(targetSchema),
    defaultValues: step1,
    values: step1,
    resetOptions: { keepDirtyValues: true },
    mode: 'onChange',
  })
  const { setError, getValues, setValue, trigger } = form
  const { register, handleSubmit, control } = form
  const { touchedFields, errors, isValid, isSubmitting, isSubmitted } = useFormState({ control })
  const [bridgeValues, setBridgeValues] = useState(null)
  const [bridgeOpen, setBridgeOpen] = useState(false)
  const [isLocking, setIsLocking] = useState(false)
  const askRobChat = useAskRobChat({
    initialMessages: stepOneFlowVersion === 'legacy_v1' ? [] : step1.askRobConversation,
  })
  const watchedTargetValues = useWatch({
    control,
    name: TARGET_FIELDS,
    defaultValue: TARGET_FIELDS.map((field) => cleanTargetValue(step1[field])),
  })
  const currentTargetValues = targetValuesFromArray(watchedTargetValues)
  const restoredTargetValues = TARGET_FIELDS.map((field) => cleanTargetValue(step1[field])).join('|')
  const bridgeMatchesCurrentTargets = targetValuesMatch(bridgeValues, currentTargetValues)
  const bridge = bridgeMatchesCurrentTargets ? buildTargetBridge(bridgeValues) : null
  const hasPendingAskRobSuggestions = askRobChat.messages.some((message) => (
    (message.fieldSuggestions || []).some((suggestion) => suggestion.status === 'pending')
  ))
  const hasPositiveCashflowTarget = numericTargetValue(currentTargetValues.targetMonthlyCashflowGross) > 0
  const canOpenBridge = isValid && hasPositiveCashflowTarget
  const isBusy = isSubmitting || isLocking

  useEffect(() => {
    if (!targetValuesPresent(targetValuesFromObject(step1))) return

    void trigger()
  }, [restoredTargetValues, step1, trigger])
  const forceTouched = (name) => touchedFields[name] || isSubmitted
  const numericRegister = (name) => {
    const field = register(name)
    return {
      ...field,
      inputMode: 'numeric',
      onChange: (event) => {
        event.target.value = digitsOnly(event.target.value)
        field.onChange(event)
      },
      pattern: INPUT_PATTERNS.integer,
    }
  }

  const onSubmit = handleSubmit(async (values) => {
    setBridgeValues(targetValuesFromObject(values))
    setBridgeOpen(true)
  })

  const continueFromBridge = async () => {
    if (!bridge) return

    try {
      setIsLocking(true)
      await saveStep(1, {
        ...bridge.values,
        targetsLocked: true,
      })
      askRobChat.clearMessages()
      setBridgeOpen(false)
      navigate('/activation/step/2')
    } catch (error) {
      setError('root', { message: error.message })
    } finally {
      setIsLocking(false)
    }
  }

  const applySuggestion = (messageIndex, suggestionId) => {
    const suggestion = askRobChat.findSuggestion(messageIndex, suggestionId)
    if (!suggestion || suggestion.status === 'applied' || suggestion.status === 'dismissed') return

    setValue(suggestion.field, suggestion.suggestedValue ?? '', {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    })
    askRobChat.updateSuggestionStatus(messageIndex, suggestionId, 'applied')
  }

  const dismissSuggestion = askRobChat.dismissSuggestion

  const askRob = async (message) => {
    await askRobChat.submitMessage(message, ({ message: userMessage, conversation }) => (
      askStepOneAgent({
        message: userMessage,
        conversation,
        currentValues: targetValuesFromObject(getValues()),
      })
    ))
  }

  return (
    <form onSubmit={onSubmit} noValidate className='animate-in fade-in slide-in-from-bottom-4 duration-300'>
      <div className='mx-auto w-full max-w-7xl px-6 py-12 sm:px-8 lg:px-12'>
        <StepHeader
          step={1}
          title='Set Investment Target'
          description='Define the financial parameters that will drive your acquisition strategy.'
        />

        <div className='grid grid-cols-1 gap-12 lg:grid-cols-5'>
          <div className='space-y-6 lg:col-span-3'>
            <LabeledInput
              label='Target Monthly Cashflow'
              required
              prefix='£'
              helper='Gross monthly income target from your property portfolio'
              placeholder='e.g. 5,000'
              {...numericRegister('targetMonthlyCashflowGross')}
              touched={forceTouched('targetMonthlyCashflowGross')}
              error={errors.targetMonthlyCashflowGross}
            />
            <LabeledInput
              label='Timeframe'
              required
              helper='Time horizon in years'
              placeholder='e.g. 5'
              {...numericRegister('timeframeYears')}
              touched={forceTouched('timeframeYears')}
              error={errors.timeframeYears}
            />
            <LabeledInput
              label='Available Capital'
              required
              prefix='£'
              helper='Capital available for immediate deployment'
              placeholder='e.g. 50,000'
              {...numericRegister('availableCapital')}
              touched={forceTouched('availableCapital')}
              error={errors.availableCapital}
            />
            <LabeledInput
              label='Joint Venture Capital'
              required
              prefix='£'
              helper='Capital accessible through partners or investors'
              placeholder='e.g. 50,000'
              {...numericRegister('jvCapitalAccess')}
              touched={forceTouched('jvCapitalAccess')}
              error={errors.jvCapitalAccess}
            />
            {(errors.root?.message || serverError) && (
              <p className='text-sm text-destructive'>{errors.root?.message || serverError}</p>
            )}
            {hasPendingAskRobSuggestions ? (
              <p className='text-sm font-medium text-muted-foreground'>
                Ask Rob has suggested edits you can apply before continuing.
              </p>
            ) : null}
          </div>

          <div className='lg:col-span-2'>
            <AskRobPanel
              subtitle='Get guidance on defining realistic investment targets'
              prompts={PROMPTS}
              messages={askRobChat.messages}
              isLoading={askRobChat.isLoading}
              error={askRobChat.error}
              onAsk={askRob}
              onApplySuggestion={applySuggestion}
              onDismissSuggestion={dismissSuggestion}
            />
          </div>
        </div>

        <TargetTransitionBridge
          bridge={bridge}
          open={bridgeOpen && Boolean(bridge)}
          onOpenChange={setBridgeOpen}
          onConfirm={continueFromBridge}
          isSubmitting={isBusy}
        />

        <StepNavigation
          stepNumber={1}
          canContinue={canOpenBridge}
          isSubmitting={isBusy}
          continueLabel='Continue to Strategy Selection'
          onBack={() => navigate(-1)}
        />
      </div>
    </form>
  )
}
